import { useMutation } from "@apollo/client";
import { loader } from "graphql.macro";
import React, { createContext, useContext, useEffect, useMemo, useState } from "react";
import { hasAuthParams, useAuth } from "react-oidc-context";
import { ApolloErrorViewer } from "shared/components/ApolloErrorViewer";
import { GqlResponse } from "types";
import { IMACkUser, isNoSsoTest, MackPrivilege, MackUser } from ".";
import LoggingIn from "./LoggingIn";
import LoginError from "./LoginError";
import NoSsoLogin from "./NoSsoLogin";

export interface IAuthenticationContext {
  mackUser?: MackUser | null;
}
export const AuthenticationContext = createContext<IAuthenticationContext>({});

const SIGN_IN_USER = loader("./graphql/mutation-sign-in-user.graphql");

type TSignInResponse = GqlResponse<IMACkUser, "user">;

export const AuthenticationProvider = (props: { children: React.ReactNode }) => {
  const auth = useAuth();
  const [hasTriedSignin, setHasTriedSignin] = useState(false);
  const [mackUser, setMackUser] = useState<MackUser | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const [signInUser, { error: mackSignInError }] = useMutation<TSignInResponse>(SIGN_IN_USER, {
    onCompleted: data => {
      if (data?.user?.status === "ACTIVE") {
        setMackUser(new MackUser(data.user));
        setIsLoading(false);
      }
    },
    onError: () => setIsLoading(false)
  });

  const providerValue = useMemo(
    () => ({
      mackUser
    }),
    [mackUser]
  );

  useEffect(() => {
    const isPendingAuth = !auth.isAuthenticated && !auth.activeNavigator && !auth.isLoading;
    if (!hasAuthParams() && isPendingAuth && !hasTriedSignin && !isNoSsoTest) {
      auth.signinRedirect();
      setHasTriedSignin(true);
    }
  }, [auth, hasTriedSignin]);

  useEffect(() => {
    if (auth.isAuthenticated) {
      signInUser();
    }
  }, [auth.isAuthenticated, auth.events, signInUser]);

  if (isNoSsoTest && !mackUser) {
    return <NoSsoLogin onLogin={() => signInUser()} />;
  }

  if (isLoading) {
    return <LoggingIn />;
  }

  if (auth.error) {
    return (
      <LoginError
        error={{
          errorCode: auth.error.cause as string,
          errorDescription: `Login error: ${auth.error.message}`
        }}
      />
    );
  }

  if (mackSignInError) {
    return <ApolloErrorViewer error={mackSignInError} />;
  }

  if (!mackUser?.hasPrivilege(MackPrivilege.Login)) {
    return (
      <LoginError
        error={{
          errorCode: "Access Denied",
          errorDescription: "Not a MACk User, Please contact support"
        }}
      />
    );
  }

  return (
    <AuthenticationContext.Provider value={providerValue}>
      {props.children}
    </AuthenticationContext.Provider>
  );
};

export function useMackAuth() {
  const context = useContext<IAuthenticationContext>(AuthenticationContext);
  if (!context) {
    throw new Error("Invalid authentication context");
  }
  return context;
}
